import './style.scss';

import cn from 'classnames';
import { InputField } from 'fields/base/InputField/InputField';
import { FormikErrors, FormikTouched } from 'formik';
import { motion } from 'framer-motion';
import { memo, useCallback } from 'react';

import { ArbitrageCexAccountsTable, ArbitrageDexWalletsTable } from 'tables/arbitrage';
import { IArbitrageCexPairSettings, IArbitrageDexPairSettings } from 'types/arbitrage';
import { ICexPair, IDexPair } from 'types/pairs';
import { Spinner } from 'ui/Spinner/Spinner';
import { Switcher } from 'ui/Switcher/Switcher';

import { useCexAccountsWithBalances } from './useCexAccountsWithBalances';

interface Props<T extends IArbitrageCexPairSettings | IArbitrageDexPairSettings> {
  cexPair?: ICexPair;
  dexPair?: IDexPair;
  values: T;
  setFieldValue: (v: string, va: any) => any;
  touched: FormikTouched<T>;
  errors: FormikErrors<T>;
  setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void;
}

const PairSettingsFormComponent = <
  T extends IArbitrageCexPairSettings | IArbitrageDexPairSettings,
>({
  cexPair,
  dexPair,
  values,
  setFieldValue,
  touched,
  errors,
  setFieldTouched,
}: Props<T>) => {
  const isCexSettings = useCallback(
    (
      values: IArbitrageCexPairSettings | IArbitrageDexPairSettings,
    ): values is IArbitrageCexPairSettings => {
      if (cexPair) {
        // Pair type comes from the modal props, so prefer it over mutable form values
        return true;
      }

      if (dexPair) {
        return false;
      }

      return 'accountId' in values;
    },
    [cexPair, dexPair],
  );

  const isCex = isCexSettings(values);
  const {
    data: pairAccounts = [],
    isLoading: isAccountsLoading,
    isFetching: isAccountsFetching,
    projectsLoaded,
  } = useCexAccountsWithBalances({ cexPair, enabled: isCex });
  const isLoading = isAccountsLoading || isAccountsFetching || (isCex && !projectsLoaded);

  const handleSelectAccount = (accountId: number | undefined) => {
    setFieldTouched('accountId', true, true);

    if (isCex) {
      setFieldValue('accountId', accountId !== values.accountId ? accountId : undefined);
    }
  };

  const handleSelectWallet = useCallback(
    (walletId: number | undefined) => {
      setFieldTouched('walletId', true, true);

      if (!isCex) {
        setFieldValue('walletId', walletId !== (values as any).walletId ? walletId : undefined);
      }
    },
    [setFieldTouched, isCex, values, setFieldValue],
  );

  return (
    <div className="arbitrage-pair-settings-form">
      <form className="settings-form">
        <span className="form-title">Settings</span>
        <div className="additional-inputs">
          <div className="boolean-inputs-container">
            <div className="boolean-input">
              Arbitrable
              <Switcher
                value={values.arbitrable ?? false}
                onChange={v => {
                  setFieldTouched('arbitrable', true, true);
                  setFieldValue('arbitrable', v);
                }}
              />
            </div>
            <div className="boolean-input">
              Subscribe to updates
              <Switcher
                value={values.subscribe_to_updates ?? false}
                onChange={v => {
                  setFieldTouched('subscribe_to_updates', true, true);
                  setFieldValue('subscribe_to_updates', v);
                }}
              />
            </div>
            <div className="boolean-input">
              Allow rebalancer buy
              <Switcher
                value={values.allow_rebalancer_buy ?? false}
                onChange={v => {
                  setFieldTouched('allow_rebalancer_buy', true, true);
                  setFieldValue('allow_rebalancer_buy', v);
                }}
              />
            </div>
            <div className="boolean-input">
              Allow rebalancer sell
              <Switcher
                value={values.allow_rebalancer_sell ?? false}
                onChange={v => {
                  setFieldTouched('allow_rebalancer_sell', true, true);
                  setFieldValue('allow_rebalancer_sell', v);
                }}
              />
            </div>
            <div className="boolean-input">
              Check balance
              <Switcher
                value={values.check_balance ?? false}
                onChange={v => {
                  setFieldTouched('check_balance', true, true);
                  setFieldValue('check_balance', v);
                }}
              />
            </div>
            <div className="boolean-input">
              Reserved balances in percentage
              <Switcher
                value={values.is_reserved_balance_base_percentage ?? false}
                onChange={v => {
                  setFieldTouched('is_reserved_balance_base_percentage', true, true);
                  setFieldValue('is_reserved_balance_base_percentage', v);
                }}
              />
            </div>
          </div>
          <div className="decimal-inputs-container">
            <InputField
              label="Target base"
              type="decimal-number"
              value={values.target_base ?? ''}
              setValue={v => {
                setFieldTouched('target_base', true, true);
                setFieldValue('target_base', v);
              }}
              errorMessage={touched.target_base ? (errors.target_base as string) : undefined}
              nodeRight={
                <div className="percentage-switch">
                  <span
                    className={cn({ selected: values.is_target_base_percentage === true })}
                    onClick={() => {
                      setFieldValue('is_target_base_percentage', true);
                      setFieldValue('target_base', '');
                    }}
                  >
                    %
                  </span>{' '}
                  /{' '}
                  <span
                    className={cn({ selected: values.is_target_base_percentage === false })}
                    onClick={() => {
                      setFieldValue('is_target_base_percentage', false);
                      setFieldValue('target_base', '');
                    }}
                  >
                    {(cexPair ?? dexPair)?.token_base.symbol}
                  </span>
                </div>
              }
            />
            {!isCex && (
              <InputField
                label="Slippage percent"
                type="decimal-number"
                value={values.slippage_percent ?? ''}
                setValue={v => {
                  setFieldTouched('slippage_percent', true, true);
                  setFieldValue('slippage_percent', v);
                }}
                errorMessage={
                  //@ts-ignore
                  touched.slippage_percent ? (errors.slippage_percent as string) : undefined
                }
              />
            )}
            {values.is_reserved_balance_base_percentage ? (
              <InputField
                label="Reserved balance %"
                type="decimal-number"
                value={values.reserved_balance_base ?? '0'}
                setValue={v => {
                  setFieldTouched('reserved_balance_base', true, true);
                  setFieldValue('reserved_balance_base', v);
                }}
                errorMessage={
                  touched.reserved_balance_base
                    ? (errors.reserved_balance_base as string)
                    : undefined
                }
              />
            ) : (
              <>
                <InputField
                  label={`Reserved balance base (${(cexPair ?? dexPair)?.token_base.symbol})`}
                  type="decimal-number"
                  value={values.reserved_balance_base ?? '0'}
                  setValue={v => {
                    setFieldTouched('reserved_balance_base', true, true);
                    setFieldValue('reserved_balance_base', v);
                  }}
                  errorMessage={
                    touched.reserved_balance_base
                      ? (errors.reserved_balance_base as string)
                      : undefined
                  }
                />
                <InputField
                  label={`Reserved balance quote (${(cexPair ?? dexPair)?.token_quote.symbol})`}
                  type="decimal-number"
                  value={values.reserved_balance_quote ?? '0'}
                  setValue={v => {
                    setFieldTouched('reserved_balance_quote', true, true);
                    setFieldValue('reserved_balance_quote', v);
                  }}
                  errorMessage={
                    touched.reserved_balance_quote
                      ? (errors.reserved_balance_quote as string)
                      : undefined
                  }
                />
              </>
            )}
          </div>
        </div>
      </form>

      {isCex && cexPair && (
        <>
          {isLoading ? (
            <div className="spinner-container">
              <Spinner size="mini" />
            </div>
          ) : pairAccounts.length === 0 ? (
            <span className="no-accounts">
              No available accounts. You cannot proceed further. You should remove this pair from
              arbitrage
            </span>
          ) : (
            <div className="account-table-wrapper">
              <div className="pair-accounts">
                <ArbitrageCexAccountsTable
                  selectedAccountId={values.accountId}
                  onSelectAccount={handleSelectAccount}
                  pair={cexPair}
                  pairAccounts={pairAccounts}
                />
              </div>
              {(touched as any).accountId && (errors as any).accountId && (
                <motion.div
                  className="accouns-error"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  {(errors as any).accountId}
                </motion.div>
              )}
            </div>
          )}
        </>
      )}
      {!isCex && dexPair && (
        <div className="account-table-wrapper">
          <ArbitrageDexWalletsTable
            pair={dexPair}
            selectedWalletId={values.walletId}
            onSelectWalletId={handleSelectWallet}
          />
          {(touched as any).walletId && (errors as any).walletId && (
            <motion.div
              className="accouns-error"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              {(errors as any).walletId}
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
};

const PairSettingsForm = memo(PairSettingsFormComponent);

export { PairSettingsForm };
